/**
 * Locks `document.body` scroll while compensating for the scrollbar width
 * via `padding-right`, so opening a Sheet does not cause page content to
 * shift horizontally when the scrollbar disappears.
 *
 * Stack-aware: multiple consumers can lock independently and the body is
 * only released once the last one unlocks.
 *
 * Used instead of reka-ui's `useBodyScrollLock` because that variant also
 * sets `body { pointer-events: none }`, which assumes a paired
 * `DialogOverlayImpl` / `DialogContent` to re-enable pointer events on the
 * content. Our `SheetContent` does not, so non-modal sheets (e.g. ApiDocumentationDrawer,
 * TdEmissionCompare) would become uninteractive. This implementation only
 * touches `overflow` and `padding-right`.
 *
 * Every overlay in the kit locks through this one stack (see `OverlayBackdrop`).
 * Mixing it with reka's stack is what leaves the page unscrollable: each keeps
 * its own snapshot of the original `overflow`, so whichever unlocks last writes
 * back a stale `hidden`.
 */
import type { WritableComputedRef } from 'vue'
import { createSharedComposable, tryOnScopeDispose } from '@vueuse/core'
import { computed, ref, watch } from 'vue'

const useBodyLockStack = createSharedComposable(() => {
  const consumers = ref(new Map<string, boolean>())
  const initial = ref<{ overflow: string; paddingRight: string } | null>(null)

  const isLocked = computed(() => {
    for (const v of consumers.value.values()) {
      if (v) {
        return true
      }
    }
    return false
  })

  function restore() {
    if (typeof document === 'undefined' || !initial.value) {
      return
    }

    document.body.style.overflow = initial.value.overflow
    document.body.style.paddingRight = initial.value.paddingRight
    document.documentElement.style.removeProperty('--scrollbar-width')
    initial.value = null
  }

  watch(
    isLocked,
    (locked, wasLocked) => {
      if (typeof document === 'undefined') {
        return
      }
      if (locked && !wasLocked) {
        initial.value = {
          overflow: document.body.style.overflow,
          paddingRight: document.body.style.paddingRight,
        }
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
        if (scrollbarWidth > 0) {
          document.body.style.paddingRight = `${scrollbarWidth}px`
          document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`)
        }
        document.body.style.overflow = 'hidden'
      } else if (!locked && wasLocked) {
        restore()
      }
    },
    { flush: 'sync' },
  )

  tryOnScopeDispose(restore)

  return consumers
})

export function useBodyScrollLock(initialState = false): WritableComputedRef<boolean> {
  const id = Math.random().toString(36).slice(2, 9)
  const consumers = useBodyLockStack()
  consumers.value.set(id, initialState)

  tryOnScopeDispose(() => {
    consumers.value.delete(id)
  })

  return computed({
    get: () => consumers.value.get(id) ?? false,
    set: (value) => consumers.value.set(id, value),
  })
}
